import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api_middleware";

export interface RoleplayResponse {
  id: string;
  resource_key: string | null;
  organization_id: string;
  creator_user_id: string;
  environment: string;
  creation_time: string;
  modified_time: string;
  display_title: string | null;
  description: string;
  human_role: string;
  bot_role: string;
  greeting: string;
  criteria_context: string;
  generated_concerns: ConcernResponse[];
  avatar_config: any | null;
  response_namespace_assets: any | null;
  faq_namespace_assets: any | null;
  emotion_guidance: string;
  valence_guidance: string;
  starting_engagement: number;
  starting_valence: number;
  starting_activation: number;
  scene_type: string;
  show_wrap: boolean;
  show_emotion_cards: boolean;
  end_upset_message: string;
  end_disengaged_message: string;
  can_swear_angry: boolean;
  expected_duration_minutes: number | null;
  order: string[];
  is_active: boolean;
  control_graph: any | null;
}

export interface ConcernResponse {
  id: string;
  question: string;
  context: string;
  bonus: boolean;
  rubric: Array<{
    score: number;
    description: string;
    example: string;
  }>;
}

export const fetchAllRoleplays = createAsyncThunk(
  "roleplays/fetchAll",
  async (params?: {
    organizationId?: string;
    environment?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.organizationId) {
      queryParams.append("organization_id", params.organizationId);
    }
    if (params?.environment) {
      queryParams.append("environment", params.environment);
    }

    const response = await api(
      `v1/admin/roleplays${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`,
      "GET"
    );
    return response;
  }
);

export const fetchRoleplay = createAsyncThunk(
  "roleplays/fetchOne",
  async ({
    roleplayId,
    organizationId,
  }: {
    roleplayId: string;
    organizationId: string;
  }) => {
    const response = await api(
      `v1/admin/roleplays/${roleplayId}?organization_id=${organizationId}`,
      "GET"
    );
    return response;
  }
);

export const fetchRoleplayHistory = createAsyncThunk(
  "roleplays/fetchHistory",
  async ({
    resourceKey,
    organizationId,
  }: {
    resourceKey: string;
    organizationId: string;
  }) => {
    const response = await api(
      `v1/admin/roleplays/history/by-key?resource_key=${resourceKey}&organization_id=${organizationId}`,
      "GET"
    );
    return response;
  }
);

export const updateRoleplayControlGraph = createAsyncThunk(
  "roleplays/updateControlGraph",
  async (params: {
    roleplayId: string;
    organizationId: string;
    control_graph: any | null;
  }) => {
    const response = await api(
      `v1/admin/roleplays/${params.roleplayId}/control-graph`,
      "PUT",
      {
        control_graph: params.control_graph,
        organization_id: params.organizationId,
      }
    );
    return response as RoleplayResponse;
  }
);

// Roleplay Links
export interface RoleplayLink {
  id: string;
  behavior_type: string;
  agent_config_id: string | null;
  created_at: string;
  creator_user_id: string;
  roleplay_id: string | null;
  roleplay_key?: string | null;
  display_name?: string | null;
  test_override_human_description?: string | null;
  additional_context?: string | null;
  namespace_asset_registry?: {
    namespace_assets: Record<string, string[]>;
  } | null;
}

export const fetchRoleplayLinks = createAsyncThunk(
  "roleplays/fetchLinks",
  async (params: { roleplayId: string; organizationId: string }) => {
    const { roleplayId, organizationId } = params;
    const response = await api(
      `v1/admin/roleplays/${roleplayId}/links?organization_id=${organizationId}`,
      "GET"
    );
    return response as RoleplayLink[];
  }
);

export interface CreateRoleplayLinkParams {
  roleplayId: string;
  organizationId: string;
  agent_config_id: string;
  display_name?: string | null;
  test_override_human_description?: string;
  additional_context?: string;
  control_graph_key?: string;
  // Note: namespace_asset_registry is not supported for roleplay links
  // RoleplayDemoInput only has roleplay_scenario_key and asset_namespace fields
}

export interface CreateRoleplayLinkResponse {
  link_id: string;
  resource_link: string;
}

export const createRoleplayLink = createAsyncThunk(
  "roleplays/createLink",
  async (params: CreateRoleplayLinkParams) => {
    const {
      roleplayId,
      organizationId,
      agent_config_id,
      display_name,
      test_override_human_description,
      additional_context,
      control_graph_key,
    } = params;
    const response = await api(
      `v1/admin/roleplays/${roleplayId}/create-link?organization_id=${organizationId}`,
      "POST",
      {
        agent_config_id,
        display_name,
        test_override_human_description,
        additional_context,
        control_graph_key,
      }
    );
    return response as CreateRoleplayLinkResponse;
  }
);

export const updateRoleplayLink = createAsyncThunk(
  "roleplays/updateLink",
  async (params: {
    linkId: string;
    organization_id: string;
    agent_config_id?: string;
    display_name?: string | null;
    test_override_human_description?: string | null;
    additional_context?: string | null;
    control_graph_key?: string | null;
    // Note: namespace_asset_registry is not supported for roleplay links
  }) => {
    const { linkId, ...updateData } = params;
    const response = await api(
      `v1/admin/roleplay-links/${linkId}`,
      "PUT",
      updateData
    );
    return response;
  }
);

// Roleplay Assessed Sessions
export interface RoleplayAssessedSession {
  id: string;
  _ts?: number;
  organization_id: string;
  user_id: string;
  conversation_id: string;
  chat_session_id: string;
  source_roleplay_id: string;
  state: "created" | "active" | "processing" | "ready";
  created_at?: string;
  processing_type: "roleplay";
  roleplay_analysis?: {
    concern_scores: Record<
      string,
      {
        score: number;
        justification: string;
      }
    >;
  } | null;
  assessment_score?: number | null;
  duration_in_min?: number | null;
  analysis_results?: any;
}

export const fetchRoleplayAssessedSessions = createAsyncThunk(
  "roleplays/fetchAssessedSessions",
  async (params: { roleplayId: string; organizationId: string }) => {
    const { roleplayId, organizationId } = params;
    const response = await api(
      `v1/admin/roleplays/${roleplayId}/assessed-sessions?organization_id=${organizationId}`,
      "GET"
    );
    return response as RoleplayAssessedSession[];
  }
);
